import { useCallback } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import formatName from 'I18n/utils/formatName';
import { useUninstallAppForUserMutation } from '../../../__generated__/chirp/com/hubspot/application/installs/rpc/ApplicationInstallsRpc.hooks';
import { GET_USER_INSTALL_INFOS_QUERY } from '../../../__generated__/chirp/com/hubspot/application/installs/rpc/ApplicationInstallsRpc.dfc.new';
import { useCaptureError } from '../useCaptureError';
import { useApp } from '../../contexts/AppContext';
import { useUninstallCallback } from '../../contexts/UninstallCallbackContext';
import { useTracking } from '../../contexts/TrackingContext';
import { EVENTS, SCREENS, ACTIONS } from '../../constants/tracking';
import { jsx as _jsx } from "react/jsx-runtime";
export function useUninstallAppForUser(user, onSuccess) {
  const {
    appId,
    appName
  } = useApp();
  const {
    onUserUninstall
  } = useUninstallCallback();
  const {
    track
  } = useTracking();
  const [mutate, {
    error,
    loading,
    client
  }] = useUninstallAppForUserMutation({
    variables: {
      appId,
      userId: user.userId
    }
  });
  const removeUserFromInstalledUsersCache = useCallback(() => {
    var _existingData$result;
    if (!client) return;
    const {
      cache
    } = client;
    const existingData = cache.readQuery({
      query: GET_USER_INSTALL_INFOS_QUERY,
      variables: {
        appId
      }
    });
    const infoForInstalledUsers = existingData === null || existingData === void 0 || (_existingData$result = existingData.result) === null || _existingData$result === void 0 ? void 0 : _existingData$result.infoForInstalledUsers;
    if (infoForInstalledUsers) {
      cache.writeQuery({
        query: GET_USER_INSTALL_INFOS_QUERY,
        variables: {
          appId
        },
        data: {
          result: {
            infoForInstalledUsers: infoForInstalledUsers.filter(_user => _user.userId !== user.userId)
          }
        }
      });
    }
  }, [client, appId, user.userId]);
  useCaptureError(error, {
    message: 'Error uninstalling app for user',
    alert: {
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app-governance-lib.errorBoundary.default.title"
      }),
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app-governance-lib.uninstallModal.error",
        options: {
          user: formatName({
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email
          })
        }
      })
    }
  });
  const uninstallAppForUser = useCallback(() => mutate({
    onCompleted: () => {
      track(EVENTS.PANEL_INTERACTED, {
        screen: SCREENS.MANAGEMENT_MODAL_PANEL,
        action: ACTIONS.USER_UNINSTALLED,
        appId,
        appName
      });
      removeUserFromInstalledUsersCache();
      onUserUninstall === null || onUserUninstall === void 0 || onUserUninstall(user);
      onSuccess === null || onSuccess === void 0 || onSuccess();
    }
  }), [mutate, removeUserFromInstalledUsersCache, onUserUninstall, onSuccess, user, track, appId, appName]);
  return {
    uninstallAppForUser,
    error,
    loading
  };
}